/**
 * shadcn/ui 標準形の Tooltip（@radix-ui/react-tooltip ラッパ）。
 *
 * PC 前提の補足説明に使う（フォームラベルの説明をレイアウトを崩さず提示する等）。
 * タッチ環境ではホバーが無いため、必須情報は tooltip に置かないこと。
 */
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { CircleHelp } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../../lib/lib'

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      className={cn(
        // bg-primary/text-primary-foreground だと primary が緑（submit系ボタンと同色）のため、
        // 補足説明であるツールチップが「送信アクション」に見えてしまう（性質誤認・視認性の指摘 user FB 2026-08-20）。
        // 慣例通りニュートラルな濃色に統一する。foreground/background の反転は使えない
        // （--foreground は生の値参照で、.dark 側が HSL 三つ組のため dark で無効色になる）ので、
        // 専用トークン --tooltip / --tooltip-foreground を使う。
        'z-50 max-w-xs overflow-hidden rounded-md bg-tooltip px-3 py-1.5 text-xs text-tooltip-foreground',
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        className,
      )}
      ref={ref}
      sideOffset={sideOffset}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * ラベル横に置く「? アイコン + ホバー説明」。フォーム項目の補足説明用の定型。
 * aria-label に説明本文を持たせるため、スクリーンリーダー・テストからも内容に到達できる。
 */
const InfoTooltip: React.FC<{ content: string; className?: string }> = ({ content, className }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger
        aria-label={content}
        className={cn('inline-flex align-middle text-muted-foreground', className)}
        type="button"
      >
        <CircleHelp aria-hidden="true" className="h-3.5 w-3.5" />
      </TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

export { InfoTooltip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
